/**
 * ProfileTab — the ContactRail's third tab (Expansion Phase G): who this
 * patient IS, as opposed to what to do next (Next steps) or the live screen
 * (Screening). Completeness meter + what's-missing hints, medications,
 * diagnoses, call ratings, web-screener answers (absorbed from the Screening
 * tab), per-study participation history, and automation enrollments (moved
 * from Next steps — enrollment state is history, not a next step).
 */
import { ReactNode, useState } from 'react';
import { FileDown } from 'lucide-react';
import { ContactDetailResponse, Enrollment } from '../../types';
import ScreenerPanel from '../ScreenerPanel';
import MedicalRecordsCard from '../records/MedicalRecordsCard';
import EligibilityReviewCard from '../eligibility/EligibilityReviewCard';
import ProfileListCard from './ProfileListCard';
import InterestListCard from './InterestListCard';
import LabsCard from './LabsCard';
import RatingsCard from './RatingsCard';
import ParticipationHistoryCard from './ParticipationHistoryCard';
import EnrollmentsCard from './EnrollmentsCard';
import PrescreeningSummaryModal from './PrescreeningSummaryModal';

const MISSING_HINTS: Record<string, string> = {
  phone: 'Add a phone number',
  email: 'Add an email address',
  medications: 'Record their medications',
  conditions: 'Record their diagnoses',
  completed_form: 'Have them complete a form',
  answers: 'No screener answers yet — run a prescreen or send a form',
};

interface Props {
  detail: ContactDetailResponse;
  enrollments: Enrollment[];
  reloadToken: number;
  /** Debounced Realtime ping (ContactDetail) — silent refetch of the records list. */
  pingTick?: number;
  isSuperAdmin: boolean;
  reenrolling: boolean;
  onReEnroll: () => void;
  /** Rendered inside the rail's Profile panel, which owns the card chrome:
   *  collapse the nine standalone cards into one divide-y grouped list and
   *  pass frameless through to every child. Absent = today's stack of
   *  separately-chromed cards, byte for byte. */
  frameless?: boolean;
}

export default function ProfileTab({
  detail, enrollments, reloadToken, pingTick = 0, isSuperAdmin, reenrolling, onReEnroll, frameless = false,
}: Props) {
  const c = detail.contact;
  const completeness = detail.profile_completeness || null;
  const [exportOpen, setExportOpen] = useState(false);

  /** Frameless mode collapses the nine blocks below into one divide-y
   *  grouped list; a no-op passthrough otherwise so the classic stack
   *  renders byte-identical to today. */
  const group = (node: ReactNode): ReactNode =>
    frameless ? <div className="py-4 first:pt-0 last:pb-0 empty:hidden">{node}</div> : node;

  return (
    <div className={frameless ? 'divide-y divide-gray-100 dark:divide-gray-700' : 'space-y-4'}>
      {group(
        <>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Profile</h3>
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              <FileDown className="h-4 w-4" />
              Export prescreening summary
            </button>
          </div>
          {exportOpen && <PrescreeningSummaryModal contactId={c.id} onClose={() => setExportOpen(false)} />}
        </>
      )}

      {completeness && completeness.missing.length > 0 && group(
        <div className={frameless ? '' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800'}>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Profile completeness</h3>
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">{completeness.percent}%</span>
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
            role="progressbar"
            aria-valuenow={completeness.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Profile completeness"
          >
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${completeness.percent}%` }} />
          </div>
          <ul className="mt-2 space-y-0.5">
            {completeness.missing.map((key) => (
              <li key={key} className="text-xs text-gray-500 dark:text-gray-400">· {MISSING_HINTS[key] || key}</li>
            ))}
          </ul>
        </div>
      )}

      {/* The source documents the meds/diagnoses/labs below are extracted from —
          faxed-in records + files uploaded here (reviewed by the nightly AI). */}
      {group(<MedicalRecordsCard contactId={c.id} reloadToken={reloadToken} pingTick={pingTick} frameless={frameless} />)}

      {/* AI eligibility reviews of everything above (records + comms + forms)
          against a study's confirmed review criteria. */}
      {group(
        <EligibilityReviewCard
          key={c.id}
          contactId={c.id}
          pingTick={pingTick}
          primaryStudyId={c.primary_study_id ?? null}
          frameless={frameless}
        />
      )}

      {/* Future-study lists: "right profile, no study open yet" (owner 2026-08-20).
          Above the meds/diagnoses stack at the owner's ask — parking a lead for a
          future study is a routine action, not archive-depth detail. */}
      {group(<InterestListCard contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {group(<ProfileListCard kind="medications" contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {group(<ProfileListCard kind="conditions" contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {group(<LabsCard contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {group(<RatingsCard contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {group(<ParticipationHistoryCard contactId={c.id} reloadToken={reloadToken} frameless={frameless} />)}
      {/* Web screener answers — absorbed here from the Screening tab. */}
      {group(<ScreenerPanel screener={detail.screener} frameless={frameless} />)}
      {group(
        <EnrollmentsCard
          enrollments={enrollments}
          isSuperAdmin={isSuperAdmin}
          reenrolling={reenrolling}
          onReEnroll={onReEnroll}
          frameless={frameless}
        />
      )}
    </div>
  );
}
